/**
 * 極簡 markdown inline parser（不裝套件）。
 *
 * 支援：
 *   **bold**         → <strong>
 *   *italic*         → <em>
 *   `code`           → <code>
 *   - item           → <ul><li>
 *   行 → <p>，空行 → 段落分隔
 *   # heading        → heading
 *
 * Output JSX 結構（不用 dangerouslySetInnerHTML，避免 XSS）。
 */

import { Fragment, type ReactNode } from 'react';

interface Token {
  type: 'text' | 'bold' | 'italic' | 'code';
  content: string;
}

/** Tokenize 一行 inline markdown。 */
function tokenizeInline(line: string): Token[] {
  const tokens: Token[] = [];
  // 用一個正則匹配 **...**, *...*, `...`，剩下的當 text
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(line)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', content: line.slice(lastIndex, match.index) });
    }
    const m = match[0];
    if (m.startsWith('**')) {
      tokens.push({ type: 'bold', content: m.slice(2, -2) });
    } else if (m.startsWith('`')) {
      tokens.push({ type: 'code', content: m.slice(1, -1) });
    } else {
      tokens.push({ type: 'italic', content: m.slice(1, -1) });
    }
    lastIndex = match.index + m.length;
  }
  if (lastIndex < line.length) {
    tokens.push({ type: 'text', content: line.slice(lastIndex) });
  }
  return tokens;
}

function renderTokens(tokens: Token[]): ReactNode {
  return tokens.map((tok, i) => {
    switch (tok.type) {
      case 'bold':
        return <strong key={i}>{tok.content}</strong>;
      case 'italic':
        return <em key={i}>{tok.content}</em>;
      case 'code':
        return (
          <code key={i} className="rounded bg-surface px-1 py-0.5 font-mono text-[0.95em]">
            {tok.content}
          </code>
        );
      default:
        return <Fragment key={i}>{tok.content}</Fragment>;
    }
  });
}

/** Render markdown string into JSX paragraphs. */
export function renderSimpleMarkdown(text: string): ReactNode {
  if (!text) return null;
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  return (
    <>
      {paragraphs.map((p, idx) => {
        // 標題：以 # / ## 開頭
        if (p.startsWith('### ')) {
          return (
            <h4 key={idx} className="mt-4 font-display text-lg font-semibold">
              {renderTokens(tokenizeInline(p.slice(4)))}
            </h4>
          );
        }
        if (p.startsWith('## ')) {
          return (
            <h3 key={idx} className="mt-5 font-display text-xl font-semibold">
              {renderTokens(tokenizeInline(p.slice(3)))}
            </h3>
          );
        }
        if (p.startsWith('# ')) {
          return (
            <h2 key={idx} className="mt-6 font-display text-2xl font-semibold">
              {renderTokens(tokenizeInline(p.slice(2)))}
            </h2>
          );
        }
        const lines = p.split('\n');
        if (lines.every((line) => /^[-*]\s+/.test(line.trim()))) {
          return (
            <ul key={idx} className="space-y-2 pl-0">
              {lines.map((line, li) => (
                <li key={li} className="grid grid-cols-[0.75rem_1fr] gap-2 leading-relaxed">
                  <span className="mt-[0.68em] h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
                  <span>{renderTokens(tokenizeInline(line.trim().replace(/^[-*]\s+/, '')))}</span>
                </li>
              ))}
            </ul>
          );
        }
        // 段落內保留單一 \n 為 <br />
        return (
          <p key={idx} className="text-pretty leading-relaxed">
            {lines.map((line, li) => (
              <Fragment key={li}>
                {renderTokens(tokenizeInline(line))}
                {li < lines.length - 1 && <br />}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
